//////////////////
// IMPORTS
//////////////////
// libraries
import React from "react";
import { Inconveniences } from "@clockwise/schema";
// attendees-pain
import { styles } from "./AttendeesPains.styles";
// util
import { Pain } from "@clockwise/client-commons/src/util/scheduling-slot";
// material-ui
import {
  Brightness3,
  Error,
  EventBusy,
  Fastfood,
  NotInterested,
  ReportProblemOutlined,
  DirectionsRun,
} from "@material-ui/icons";
import { Chip } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { isUndefined, omitBy } from "lodash";

const ICONS = {
  Brightness3: Brightness3,
  Error: Error,
  EventBusy: EventBusy,
  Fastfood: Fastfood,
  NotInterested: NotInterested,
  ReportProblemOutlined: ReportProblemOutlined,
  DirectionsRun: DirectionsRun,
};

type IconType =
  | "Brightness3"
  | "Error"
  | "EventBusy"
  | "Fastfood"
  | "NotInterested"
  | "ReportProblemOutlined"
  | "DirectionsRun";

const renderPainRow = (
  message: string,
  iconKey: IconType,
  pain: Pain,
  color?: string | undefined,
) => {
  const Icon = ICONS[iconKey];

  const additionalStyles = omitBy({ color }, isUndefined); // Remove any undefined properties so it doesn't override
  const messageStyle = {
    overflowWrap: "anywhere",
    wordBreak: "normal",
    whiteSpace: "pre-line",
    ...additionalStyles,
  } as object;
  const iconStyle = { ...styles.warningIcon, ...additionalStyles };

  return (
    <div key={`attendee-pain-${pain ? pain : Math.random()}`} style={styles.warning}>
      <Icon style={iconStyle} />
      <span style={messageStyle}>{message}</span>
    </div>
  );
};

const renderPainChip = (
  iconKey: IconType,
  numImpacted: number,
  pain: Pain,
  iconStyle: object,
  chipStyle: object,
) => {
  const Icon = ICONS[iconKey];

  return (
    <Chip
      key={pain}
      style={chipStyle}
      clickable={false}
      size="small"
      icon={<Icon style={iconStyle} />}
      label={numImpacted}
    />
  );
};

const getIconType = (pain: string): IconType => {
  switch (pain) {
    case "Conflict":
      return "NotInterested";
    case Inconveniences.MissedLunch:
      return "Fastfood";
    case Inconveniences.OutsideMeetingHours:
      return "EventBusy";
    case Inconveniences.OutsideWorkingHours:
      return "Brightness3";
    case Inconveniences.OnNoMeetingDay:
      return "EventBusy";
    case Inconveniences.External:
    case Inconveniences.Uncrawled:
      return "ReportProblemOutlined";
    case Inconveniences.NoMeetingRelief:
      return "DirectionsRun";
    case Inconveniences.Other:
    default:
      return "Error";
  }
};

export const AttendeesPainsTailwind = ({
  pains,
  iconClasses,
  rowClasses = "",
  painClasses = "",
}: {
  pains: { pain: Pain; value: string }[];
  iconClasses?: string;
  rowClasses?: string;
  painClasses?: string;
}) => {
  if (!pains?.length) {
    return <div></div>;
  }

  const renderedPains = pains.map((pain: { pain: Pain; value: string }) => {
    const Icon = ICONS[getIconType(pain.pain)];
    const painType = pain.pain;

    const messageClasses = "cw-whitespace-normal";
    const iconSizeClasses = "cw-h-[16px] cw-w-[16px] cw-mr-1";

    return (
      <div
        key={`attendee-pain-${pain ? pain : Math.random()}`}
        className={rowClasses + " cw-flex cw-items-start cw-mb-2 last:cw-mb-0"}
      >
        <Icon
          className={
            painType === "Conflict"
              ? `${iconClasses} ${painClasses} ${iconSizeClasses}`
              : iconSizeClasses
          }
        />
        <span
          className={painType === "Conflict" ? `${messageClasses} ${painClasses}` : messageClasses}
          style={{ overflowWrap: "anywhere" }}
          cw-id="attendee-pains-summary"
        >
          {pain.value}
        </span>
      </div>
    );
  });

  return <div>{renderedPains}</div>;
};

const AttendeesPainsRows = ({
  pains,
  color,
}: {
  pains: { pain: Pain; value: string }[];
  color?: string | undefined;
}) => {
  if (!pains?.length) {
    return <div></div>;
  }

  const renderedPains = pains.map((pain: { pain: Pain; value: string }) =>
    renderPainRow(pain.value, getIconType(pain.pain), pain.pain, color),
  );

  return <div>{renderedPains}</div>;
};

interface AttendeesPainsCompactProps {
  pains: { pain: Pain; value: string; numImpacted: number }[];
  renderIsSelected: boolean;
}

const AttendeesPainsCompactCmpt = ({ pains, renderIsSelected }: AttendeesPainsCompactProps) => {
  if (!pains?.length) {
    return <div></div>;
  }

  const renderedPains = pains.map((pain) =>
    renderPainChip(
      getIconType(pain.pain),
      pain.numImpacted,
      pain.pain,
      renderIsSelected ? styles.warningIconCompactSelected : styles.warningIconCompact,
      renderIsSelected ? styles.painChipSelected : styles.painChip,
    ),
  );

  return <div style={{ display: "inline-block" }}>{renderedPains}</div>;
};

export const AttendeesPains = withStyles(styles)(AttendeesPainsRows);
export const AttendeesPainsCompact = withStyles(styles)(AttendeesPainsCompactCmpt);
